import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, InputGroup, ToggleButton, ToggleButtonGroup, Badge } from 'react-bootstrap';
import { FiTag, FiClock, FiSend, FiX, FiInfo, FiPercent, FiAlertCircle, FiDollarSign } from 'react-icons/fi';
import quotationsService from '../../../../services/quotations.service';
import Swal from 'sweetalert2';

const OfferModal = ({ show, handleClose, quotation, onOfferCreated }) => {
    const [fullQuotation, setFullQuotation] = useState(null);
    const [discountMode, setDiscountMode] = useState('PORCENTAJE'); // 'PORCENTAJE' or 'MONTO'
    const [globalDiscount, setGlobalDiscount] = useState(0);
    const [itemDiscounts, setItemDiscounts] = useState({});
    const [durationHours, setDurationHours] = useState(24);
    const [durationMinutes, setDurationMinutes] = useState(0);
    const [loading, setLoading] = useState(false);
    const [previewTotal, setPreviewTotal] = useState(0);
    const [previewSubtotal, setPreviewSubtotal] = useState(0); // Before global discount

    useEffect(() => {
        const fetchFullQuotation = async () => {
            if (show && quotation && quotation.id) {
                try {
                    setLoading(true);
                    const data = await quotationsService.getById(quotation.id);
                    setFullQuotation(data);
                    
                    const initialItemDiscounts = {};
                    if (data.detalles) {
                        data.detalles.forEach(item => {
                            initialItemDiscounts[item.id] = 0;
                        });
                    }
                    setItemDiscounts(initialItemDiscounts);
                } catch (err) {
                    console.error('Error fetching full quotation:', err);
                    Swal.fire('Error', 'No se pudieron cargar los detalles de la cotización.', 'error');
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchFullQuotation();
    }, [show, quotation]);

    // SEQUENTIAL CALCULATION
    useEffect(() => {
        if (!fullQuotation || !fullQuotation.detalles) return;

        let subtotalAccumulated = 0;
        
        fullQuotation.detalles.forEach(item => {
            const baseItemTotal = parseFloat(item.precio_unitario) * item.cantidad;
            const itemInput = parseFloat(itemDiscounts[item.id]) || 0;
            
            let itemDiscountVal = 0;
            if (discountMode === 'PORCENTAJE') {
                itemDiscountVal = baseItemTotal * (itemInput / 100);
            } else {
                itemDiscountVal = itemInput;
            }
            
            const itemSubtotal = Math.max(0, baseItemTotal - itemDiscountVal);
            
            // Extract tax from product registry if possible, else use saved tax
            const currentTax = item.producto_info ? item.producto_info.impuesto_total : item.impuesto;
            const taxMultiplier = 1 + (parseFloat(currentTax) / 100);
            const itemTotalWithTax = itemSubtotal * taxMultiplier;
            
            subtotalAccumulated += itemTotalWithTax;
        });

        setPreviewSubtotal(subtotalAccumulated);

        const globalInput = parseFloat(globalDiscount) || 0;
        let globalDiscountVal = 0;
        if (discountMode === 'PORCENTAJE') {
            globalDiscountVal = subtotalAccumulated * (globalInput / 100);
        } else {
            globalDiscountVal = globalInput;
        }

        const finalTotal = Math.max(0, subtotalAccumulated - globalDiscountVal);
        setPreviewTotal(finalTotal);

    }, [fullQuotation, discountMode, globalDiscount, itemDiscounts]);

    const handleItemDiscountChange = (itemId, value) => {
        setItemDiscounts(prev => ({
            ...prev,
            [itemId]: parseFloat(value) || 0
        }));
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const data = {
                modo_descuento: discountMode,
                descuento_global: globalDiscount,
                descuentos_items: itemDiscounts,
                duracion_horas: durationHours,
                duracion_minutos: durationMinutes
            };
            await quotationsService.crearOferta(fullQuotation.id, data);
            Swal.fire({
                title: '¡Oferta Enviada!',
                text: 'La oferta ha sido creada y enviada al correo del cliente.',
                icon: 'success',
                confirmButtonColor: '#6366f1'
            });
            onOfferCreated();
        } catch (err) {
            console.error('Error creating offer:', err);
            Swal.fire('Error', 'No se pudo crear la oferta. Por favor intenta de nuevo.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);
    };

    if (!show) return null;

    return (
        <Modal 
            show={show} 
            onHide={handleClose} 
            size="lg" 
            centered 
            className="offer-modal-pro"
            backdrop="static"
        >
            <style>
                {`
                .offer-modal-pro .modal-content {
                    border: none;
                    border-radius: 20px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    background: #f8fafc;
                    overflow: hidden;
                }
                .modal-header-pro {
                    background: white;
                    border-bottom: 1px solid #e2e8f0;
                    padding: 20px 32px;
                }
                .modal-body-pro {
                    padding: 24px 32px;
                    max-height: 75vh;
                    overflow-y: auto;
                }
                .section-card {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    border: 1px solid #e2e8f0;
                    margin-bottom: 20px;
                    position: relative;
                }
                .section-title {
                    font-size: 0.75rem;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    color: #94a3b8;
                    margin-bottom: 16px;
                    display: flex;
                    align-items: center;
                }
                .product-list-container {
                    max-height: 300px;
                    overflow-y: auto;
                    border: 1px solid #f1f5f9;
                    border-radius: 10px;
                }
                .product-item {
                    display: grid;
                    grid-template-columns: 1fr auto auto;
                    align-items: center;
                    padding: 16px;
                    border-bottom: 1px solid #f1f5f9;
                    gap: 20px;
                    transition: background 0.2s;
                }
                .product-item:hover {
                    background: #fbfcfe;
                }
                .product-item:last-child {
                    border-bottom: none;
                }
                .price-breakdown {
                    font-size: 0.7rem;
                    color: #64748b;
                    margin-top: 2px;
                }
                .x-small {
                    font-size: 0.75rem;
                }
                .summary-box {
                    background: #1e293b;
                    color: white;
                    border-radius: 14px;
                    padding: 24px;
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                }
                .summary-row {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 10px;
                    font-size: 0.95rem;
                }
                .summary-row.total {
                    margin-top: 16px;
                    padding-top: 16px;
                    border-top: 2px dashed rgba(255,255,255,0.1);
                    font-weight: 800;
                    font-size: 1.5rem;
                    color: #fbbf24;
                }
                .btn-primary-pro {
                    background: #6366f1;
                    border: none;
                    padding: 14px 32px;
                    border-radius: 10px;
                    font-weight: 700;
                    transition: all 0.2s;
                }
                .btn-primary-pro:hover {
                    background: #4f46e5;
                    transform: translateY(-1px);
                }
                .badge-tax {
                    font-size: 0.65rem;
                    padding: 2px 6px;
                    background: #f1f5f9;
                    color: #475569;
                    border-radius: 4px;
                    font-weight: 600;
                }
                `}
            </style>
            
            <div className="modal-header-pro">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                        <div className="bg-primary bg-opacity-10 p-2 rounded-lg me-3" style={{ borderRadius: '12px' }}>
                            <FiTag size={24} color="#6366f1" />
                        </div>
                        <div>
                            <h5 className="mb-0 fw-extrabold" style={{ color: '#1e293b' }}>Configuración de Oferta</h5>
                            {quotation && <span className="text-muted small">ID: #{quotation.numero} • Cliente: {quotation.cliente_nombre}</span>}
                        </div>
                    </div>
                    <button className="bg-transparent border-0 p-2 text-slate-400 hover:text-slate-600 transition-colors" onClick={handleClose}>
                        <FiX size={24} />
                    </button>
                </div>

                <div className="d-flex justify-content-center">
                    <ToggleButtonGroup type="radio" name="discountMode" value={discountMode} onChange={setDiscountMode} className="bg-white rounded-lg shadow-sm">
                        <ToggleButton 
                            id="tbg-btn-1" 
                            value="PORCENTAJE" 
                            variant="outline-primary"
                            className="px-4 py-2 fw-bold"
                            style={{ borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' }}
                        >
                            <FiPercent className="me-2" /> Porcentaje (%)
                        </ToggleButton>
                        <ToggleButton 
                            id="tbg-btn-2" 
                            value="MONTO" 
                            variant="outline-primary"
                            className="px-4 py-2 fw-bold"
                            style={{ borderTopRightRadius: '10px', borderBottomRightRadius: '10px' }}
                        >
                            <FiDollarSign className="me-2" /> Monto Fijo ($)
                        </ToggleButton>
                    </ToggleButtonGroup>
                </div>
            </div>

            <div className="modal-body-pro">
                {!fullQuotation && loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-4 text-slate-500 fw-medium">Cargando detalles de la cotización...</p>
                    </div>
                ) : fullQuotation ? (
                    <Form>
                        {/* 1. Descuentos por Producto (FIRST) */}
                        <div className="section-card">
                            <h6 className="section-title">
                                <FiInfo className="me-2" size={14} /> 1. Ajuste por Producto (Incl. Impuestos)
                            </h6>
                            <div className="product-list-container">
                                {fullQuotation.detalles.map((item) => {
                                    const baseNeto = parseFloat(item.precio_unitario) * item.cantidad;
                                    const itemInput = parseFloat(itemDiscounts[item.id]) || 0;
                                    const itemDiscountVal = discountMode === 'PORCENTAJE' ? baseNeto * (itemInput / 100) : itemInput;
                                    const finalNeto = Math.max(0, baseNeto - itemDiscountVal);
                                    
                                    // Extract tax from product registry
                                    const currentTax = item.producto_info ? item.producto_info.impuesto_total : item.impuesto;
                                    const finalIVA = finalNeto * (parseFloat(currentTax) / 100);
                                    const finalTotal = finalNeto + finalIVA;

                                    return (
                                        <div key={item.id} className="product-item">
                                            <div>
                                                <div className="fw-bold text-slate-700" style={{ fontSize: '0.9rem' }}>{item.producto_nombre}</div>
                                                <div className="price-breakdown">
                                                    Base: {formatCurrency(item.precio_unitario)} (Neto) x {item.cantidad}
                                                </div>
                                            </div>
                                            <div style={{ width: '130px' }}>
                                                <InputGroup size="sm">
                                                    <InputGroup.Text className="bg-light text-muted x-small">
                                                        {discountMode === 'PORCENTAJE' ? '%' : '$'}
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        value={itemDiscounts[item.id] || 0}
                                                        onChange={(e) => handleItemDiscountChange(item.id, e.target.value)}
                                                        className="text-center fw-bold"
                                                    />
                                                </InputGroup>
                                            </div>
                                            <div className="text-end" style={{ width: '150px' }}>
                                                <div className="fw-bolder text-slate-900" style={{ fontSize: '1.05rem' }}>
                                                    {formatCurrency(finalTotal)}
                                                </div>
                                                <div className="x-small text-slate-500">
                                                    Neto: {formatCurrency(finalNeto)} <Badge className="badge-tax">+{currentTax}% Impuesto</Badge>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* 2. Descuento Global (SECOND) */}
                        <div className="section-card bg-light bg-opacity-50">
                            <h6 className="section-title">
                                <FiTag className="me-2" size={14} /> 2. Descuento Adicional Global
                            </h6>
                            <Row className="align-items-center">
                                <Col md={8}>
                                    <p className="text-muted x-small mb-0 pe-4">
                                        Este descuento se aplica sobre la suma total de los productos ya rebajados. 
                                        Ideal para campañas especiales o cierres de venta.
                                    </p>
                                </Col>
                                <Col md={4}>
                                    <InputGroup size="lg" className="shadow-sm">
                                        <InputGroup.Text className="bg-white text-muted">
                                            {discountMode === 'PORCENTAJE' ? '%' : '$'}
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            value={globalDiscount}
                                            onChange={(e) => setGlobalDiscount(Math.max(0, parseFloat(e.target.value) || 0))}
                                            placeholder="0"
                                            className="fw-bold"
                                        />
                                    </InputGroup>
                                </Col>
                            </Row>
                        </div>

                        {/* 3. Vigencia */}
                        <div className="section-card">
                            <h6 className="section-title">
                                <FiClock className="me-2" size={14} /> 3. Período de Validez
                            </h6>
                            <Row className="g-3">
                                <Col xs={6}>
                                    <Form.Label className="x-small text-slate-500 mb-1 fw-bold">Horas</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        value={durationHours} 
                                        onChange={(e) => setDurationHours(Math.max(0, parseInt(e.target.value) || 0))} 
                                        className="fw-bold"
                                    />
                                </Col>
                                <Col xs={6}>
                                    <Form.Label className="x-small text-slate-500 mb-1 fw-bold">Minutos</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        value={durationMinutes} 
                                        onChange={(e) => setDurationMinutes(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))} 
                                        className="fw-bold"
                                    />
                                </Col>
                            </Row>
                            <div className="mt-3 d-flex align-items-center text-orange-500 x-small fw-bold">
                                <FiAlertCircle className="me-2" />
                                La oferta expirará automáticamente y volverá a su precio original.
                            </div>
                        </div>

                        {/* Resumen Final Transparent */}
                        <div className="summary-box">
                            <div className="summary-row opacity-75">
                                <span>Total Productos (con Impuestos):</span>
                                <span>{formatCurrency(previewSubtotal)}</span>
                            </div>
                            <div className="summary-row text-info fw-bold">
                                <span>Descuento Global ({discountMode === 'PORCENTAJE' ? `${globalDiscount}%` : 'Monto'}):</span>
                                <span>- {formatCurrency(previewSubtotal - previewTotal)}</span>
                            </div>
                            <div className="summary-row total">
                                <span>Total Final con Impuestos:</span>
                                <span>{formatCurrency(previewTotal)}</span>
                            </div>
                            <div className="text-center mt-3">
                                <div className="d-inline-block px-3 py-1 rounded bg-success bg-opacity-20 text-success fw-bold x-small">
                                    Ahorro Total para el Cliente: {formatCurrency(fullQuotation.total - previewTotal)}
                                </div>
                            </div>
                        </div>
                    </Form>
                ) : null}
            </div>

            <div className="p-4 bg-white border-top d-flex gap-3 justify-content-end">
                <Button variant="link" onClick={handleClose} disabled={loading} className="text-slate-500 text-decoration-none fw-bold px-4">
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSubmit} 
                    disabled={loading || !fullQuotation}
                    className="btn-primary-pro d-flex align-items-center shadow-sm"
                >
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Procesando...
                        </>
                    ) : (
                        <>
                            <FiSend className="me-2" /> Enviar Oferta Especial
                        </>
                    )}
                </Button>
            </div>
        </Modal>
    );
};

export default OfferModal;
